import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Grid, TextField, Select, MenuItem, FormControl, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Alert, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link } from 'react-router-dom';
import LogTheme from './LogTheme';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthHeaders } from '../../utils/api';

const AdminDashboard = () => {
  const { userRole } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [grantData, setGrantData] = useState({ identifier: '', plan: 'premium', expiresAt: '' });
  const [identifierType, setIdentifierType] = useState('userId'); // 'userId', 'username', 'email'
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [revokingUserId, setRevokingUserId] = useState(null);
  const [deleteDialogUser, setDeleteDialogUser] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);

  useEffect(() => {
    if (userRole === 'admin' || userRole === 'superadmin') {
      fetchStats();
      fetchUsers();
    }
  }, [userRole]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/admin/stats`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/admin/users?limit=100`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Users data received:', data);
        setUsers(data.users || []);
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Okänt fel' }));
        console.error('Error fetching users:', response.status, errorData);
        setError(`Kunde inte hämta användare: ${errorData.message || 'Okänt fel'}`);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Kunde inte hämta användare. Kontrollera din internetanslutning.');
      setUsers([]);
    }
  };

  const handleGrantSubscription = async () => {
    if (!grantData.identifier || !grantData.plan) {
      setError('Identifierare och plan krävs.');
      return;
    }

    try {
      const requestBody = {
        plan: grantData.plan,
        expiresAt: grantData.expiresAt || undefined
      };
      
      // Lägg till rätt fält baserat på vald typ
      if (identifierType === 'userId') {
        requestBody.userId = grantData.identifier;
      } else if (identifierType === 'username') {
        requestBody.username = grantData.identifier;
      } else if (identifierType === 'email') {
        requestBody.email = grantData.identifier;
      }

      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/admin/grant-subscription`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        setSuccess('Prenumeration beviljad!');
        setGrantDialogOpen(false);
        setGrantData({ identifier: '', plan: 'premium', expiresAt: '' });
        setIdentifierType('userId');
        fetchUsers();
        fetchStats();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Fel vid beviljande av prenumeration.');
      }
    } catch (error) {
      setError('Något gick fel.');
    }
  };

  const handleRevokeGrantedSubscription = async (userId) => {
    if (!window.confirm('Ta bort gratis Premium från denna användare? De går tillbaka till Bas.')) {
      return;
    }
    setRevokingUserId(userId);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/admin/revoke-granted-subscription`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });
      if (response.ok) {
        setSuccess('Gratis Premium har tagits bort.');
        fetchUsers();
        fetchStats();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Kunde inte ta bort Premium.');
      }
    } catch (err) {
      setError('Något gick fel.');
    } finally {
      setRevokingUserId(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialogUser) return;
    const userId = deleteDialogUser._id;
    setDeletingUserId(userId);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setSuccess('Användaren och allt associerat innehåll har tagits bort.');
        setDeleteDialogUser(null);
        fetchUsers();
        fetchStats();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Kunde inte ta bort användaren.');
      }
    } catch (err) {
      setError('Något gick fel.');
    } finally {
      setDeletingUserId(null);
    }
  };

  if (userRole !== 'admin' && userRole !== 'superadmin') {
    return (
      <LogTheme>
        <Box padding={4} sx={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Alert severity="error">Du har inte behörighet att se denna sida.</Alert>
        </Box>
      </LogTheme>
    );
  }

  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <LogTheme>
      <Box padding={{ xs: 2, md: 4 }} sx={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Typography variant="h1" gutterBottom>
          Kontrollpanel
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {stats && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4">{stats.users.total}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Totalt antal användare
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4">{stats.users.active}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aktiva användare
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4">{stats.content.logs}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tripploggar
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4">{stats.content.serviceLogs}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Serviceloggar
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h2">Användare</Typography>
              {userRole === 'superadmin' && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setGrantDialogOpen(true)}
                >
                  Tilldela gratis prenumeration
                </Button>
              )}
            </Box>

            <TextField
              fullWidth
              label="Sök användare"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TableContainer
              component={Paper}
              sx={{ overflowX: 'auto', display: 'block', maxWidth: '100%' }}
            >
              <Table sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Användarnamn</TableCell>
                    <TableCell>E-post</TableCell>
                    <TableCell>Roll</TableCell>
                    <TableCell>Prenumeration</TableCell>
                    <TableCell>Status</TableCell>
                    {userRole === 'superadmin' && (
                      <TableCell>Åtgärder</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={userRole === 'superadmin' ? 7 : 6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {searchQuery ? 'Inga användare matchade sökningen.' : 'Inga användare hittades.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell>{user._id}</TableCell>
                        <TableCell>{user.username}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip label={user.role} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              user.isGrantedPremium
                                ? 'Premium (tilldelad)'
                                : user.subscriptionPlan === 'premium'
                                ? 'Premium (betald)'
                                : (user.subscriptionPlan || 'basic')
                            }
                            size="small"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.active ? 'aktiv' : 'ej aktiv'}
                            size="small"
                            color={user.active ? 'success' : 'default'}
                          />
                        </TableCell>
                        {userRole === 'superadmin' && (
                          <TableCell>
                            <Tooltip title="Ändra användare">
                              <IconButton
                                component={Link}
                                to={`/changeuser/${user._id}`}
                                color="primary"
                                size="small"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            {user.isGrantedPremium && (
                              <Tooltip title="Ta bort Premium">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleRevokeGrantedSubscription(user._id)}
                                  disabled={revokingUserId === user._id}
                                >
                                  <RemoveCircleOutlineIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip
                              title={
                                user.stripeSubscriptionId || user.stripeCustomerId
                                  ? 'Användare med Stripe-prenumeration kan inte tas bort'
                                  : 'Ta bort användare'
                              }
                            >
                              <span>
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => setDeleteDialogUser(user)}
                                  disabled={
                                    deletingUserId === user._id ||
                                    !!user.stripeSubscriptionId ||
                                    !!user.stripeCustomerId
                                  }
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Dialog open={!!deleteDialogUser} onClose={() => !deletingUserId && setDeleteDialogUser(null)}>
          <DialogTitle>Ta bort användare</DialogTitle>
          <DialogContent>
            <Typography>
              Är du säker på att du vill ta bort användaren {deleteDialogUser?.username} ({deleteDialogUser?.email})?
              Allt innehåll som tillhör användaren (tripploggar, serviceloggar, delad åtkomst, inbjudningar) tas också bort.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogUser(null)} disabled={!!deletingUserId}>
              Avbryt
            </Button>
            <Button onClick={handleDeleteUser} color="error" variant="contained" disabled={!!deletingUserId}>
              Ta bort
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={grantDialogOpen} onClose={() => setGrantDialogOpen(false)}>
          <DialogTitle>Ge gratis prenumeration</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel>Identifierare-typ</InputLabel>
              <Select
                value={identifierType}
                label="Identifierare-typ"
                onChange={(e) => setIdentifierType(e.target.value)}
              >
                <MenuItem value="userId">Användar-ID</MenuItem>
                <MenuItem value="username">Användarnamn</MenuItem>
                <MenuItem value="email">E-post</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label={identifierType === 'userId' ? 'Användar-ID' : identifierType === 'username' ? 'Användarnamn' : 'E-post'}
              value={grantData.identifier}
              onChange={(e) => setGrantData({ ...grantData, identifier: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Plan</InputLabel>
              <Select
                value={grantData.plan}
                label="Plan"
                onChange={(e) => setGrantData({ ...grantData, plan: e.target.value })}
              >
                <MenuItem value="premium">Premium</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Förfaller (valfritt)"
              type="date"
              value={grantData.expiresAt}
              onChange={(e) => setGrantData({ ...grantData, expiresAt: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGrantDialogOpen(false)}>Avbryt</Button>
            <Button onClick={handleGrantSubscription} variant="contained">
              Ge prenumeration
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LogTheme>
  );
};

export default AdminDashboard;



